/**
 * PromptLimitModal — warns users when their assembled prompt (including upstream
 * context) exceeds the model's character limit.
 *
 * Offers two choices:
 *   • Cancel   — the user goes back to edit the prompt manually.
 *   • Optimize — the full text is condensed via AI and the result is returned to
 *                the caller, which updates the node's prompt field.
 *
 * Uses the same imperative-service pattern as ConfirmDialog / toastService.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CompressIcon from '@mui/icons-material/Compress';
import CloseIcon from '@mui/icons-material/Close';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PromptLimitCheckResult {
  action: 'cancel' | 'condensed';
  condensedText?: string;
}

export interface PromptLimitCheckOptions {
  /** The full assembled text to be sent to the model (prompt + any piped context). */
  fullText: string;
  /** The model's hard character limit. */
  limit: number;
  /** Human-readable model name shown in the modal. */
  modelName: string;
  /** Characters coming from upstream connected nodes (context piped in). */
  contextChars: number;
  /** Characters in the user's own prompt field. */
  promptChars: number;
  /**
   * Condense function provided by the caller.
   * Receives the full assembled text and returns the condensed version.
   */
  onCondense: (text: string) => Promise<string>;
}

type PromptLimitCallback = (result: PromptLimitCheckResult) => void;

interface PromptLimitRequest extends PromptLimitCheckOptions {
  callback: PromptLimitCallback;
}

// ── Modal Component ───────────────────────────────────────────────────────────

interface PromptLimitModalProps extends PromptLimitCheckOptions {
  onResult: (result: PromptLimitCheckResult) => void;
}

const PromptLimitModal: React.FC<PromptLimitModalProps> = ({
  fullText,
  limit,
  modelName,
  contextChars,
  promptChars,
  onCondense,
  onResult,
}) => {
  const [isCondensing, setIsCondensing] = useState(false);
  const [condenseError, setCondenseError] = useState<string | null>(null);

  const totalChars = fullText.length;
  const overBy = totalChars - limit;
  const hasContext = contextChars > 0;
  const fmt = (n: number) => n.toLocaleString();

  const handleOptimize = async () => {
    setIsCondensing(true);
    setCondenseError(null);
    try {
      const condensedText = await onCondense(fullText);
      onResult({ action: 'condensed', condensedText });
    } catch (err: any) {
      setCondenseError(err?.message || 'Failed to condense text. Please try again.');
    } finally {
      setIsCondensing(false);
    }
  };

  const handleCancel = () => {
    if (!isCondensing) onResult({ action: 'cancel' });
  };

  return ReactDOM.createPortal(
    // aria-modal also makes canvas undo/redo stand down (isCanvasHistoryBlocked):
    // this modal opens before the node is registered as generating, so an undo
    // here could remove the node and "send anyway" would pay for a node that is gone.
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
        backdropFilter: 'blur(4px)',
      }}
      onClick={handleCancel}
    >
      <div
        style={{
          backgroundColor: 'var(--color-bg-node-body)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          minWidth: '340px',
          maxWidth: '480px',
          width: '90%',
          boxShadow:
            '0 8px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(245, 158, 11, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '16px',
          }}
        >
          <WarningAmberIcon
            style={{ color: '#f59e0b', fontSize: '24px', flexShrink: 0 }}
          />
          <h3
            style={{
              margin: 0,
              color: 'var(--color-text-primary)',
              fontSize: 'var(--font-size-md)',
              fontWeight: 600,
              flex: 1,
            }}
          >
            Prompt Too Long
          </h3>
          <button
            onClick={handleCancel}
            disabled={isCondensing}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-tertiary)',
              cursor: isCondensing ? 'not-allowed' : 'pointer',
              padding: '2px',
              lineHeight: 1,
              opacity: isCondensing ? 0.4 : 1,
            }}
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
        </div>

        {/* ── Info line ──────────────────────────────────────── */}
        <p
          style={{
            margin: '0 0 12px',
            color: 'var(--color-text-secondary)',
            fontSize: 'var(--font-size-xs)',
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: 'var(--color-text-primary)' }}>
            {modelName}
          </strong>{' '}
          accepts up to{' '}
          <strong style={{ color: 'var(--color-text-primary)' }}>
            {fmt(limit)}
          </strong>{' '}
          characters.
        </p>

        {/* ── Character breakdown ────────────────────────────── */}
        <div
          style={{
            background: 'var(--color-bg-control)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            marginBottom: '12px',
            fontSize: 'var(--font-size-xs)',
            lineHeight: 1.8,
          }}
        >
          {hasContext && (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <span>Your prompt</span>
                <span>{fmt(promptChars)} chars</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <span>Upstream context (piped in)</span>
                <span>{fmt(contextChars)} chars</span>
              </div>
              <div
                style={{
                  height: '1px',
                  background: 'var(--color-border)',
                  margin: '6px 0',
                }}
              />
            </>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
            }}
          >
            <span>Total being sent</span>
            <span style={{ color: '#ef4444' }}>{fmt(totalChars)} chars</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: '#ef4444',
              marginTop: '2px',
            }}
          >
            <span>Over the limit by</span>
            <span>{fmt(overBy)} chars</span>
          </div>
        </div>

        {/* ── Context note ───────────────────────────────────── */}
        {hasContext && (
          <p
            style={{
              margin: '0 0 14px',
              color: 'var(--color-text-tertiary)',
              fontSize: '11px',
              lineHeight: 1.5,
            }}
          >
            The character limit includes context piped in from connected nodes.
          </p>
        )}

        {/* ── Condense error ─────────────────────────────────── */}
        {condenseError && (
          <p
            style={{
              margin: '0 0 12px',
              color: '#ef4444',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            ⚠ {condenseError}
          </p>
        )}

        {/* ── Actions ────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleCancel}
            disabled={isCondensing}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-primary)',
              cursor: isCondensing ? 'not-allowed' : 'pointer',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
              opacity: isCondensing ? 0.5 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleOptimize}
            disabled={isCondensing}
            style={{
              padding: '8px 14px',
              background: 'var(--color-accent-primary)',
              border: '1px solid var(--color-accent-primary)',
              borderRadius: 'var(--radius-md)',
              color: 'white',
              cursor: isCondensing ? 'not-allowed' : 'pointer',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: isCondensing ? 0.85 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            {isCondensing ? (
              <>
                <span
                  style={{
                    width: '13px',
                    height: '13px',
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'plm-spin 0.65s linear infinite',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                Optimizing…
              </>
            ) : (
              <>
                <CompressIcon style={{ fontSize: 15 }} />
                Optimize with Condense
              </>
            )}
          </button>
        </div>
      </div>

      {/* Spinner keyframe injected inline so no separate CSS file is needed */}
      <style>{`@keyframes plm-spin { to { transform: rotate(360deg); } }`}</style>
    </div>,
    document.body
  );
};

// ── Service (singleton, same pattern as ConfirmDialogService) ─────────────────

type PromptLimitListener = (request: PromptLimitRequest) => void;

class PromptLimitService {
  private listeners: PromptLimitListener[] = [];

  /**
   * Show the prompt–limit modal and wait for the user's choice.
   * Returns a promise that resolves to `{ action: 'cancel' }` or
   * `{ action: 'condensed', condensedText: '...' }`.
   */
  check(options: PromptLimitCheckOptions): Promise<PromptLimitCheckResult> {
    return new Promise((resolve) => {
      const request: PromptLimitRequest = { ...options, callback: resolve };
      this.listeners.forEach((l) => l(request));
    });
  }

  subscribe(listener: PromptLimitListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }
}

export const promptLimitService = new PromptLimitService();
export default PromptLimitModal;
